import { formatMonto } from "../../../shared";
import { montoDe } from "./calculos";
import { aFecha, diasEntre, fechaLocal } from "./periodos";

// Agregación de los renglones del panel por una medida y una dimensión, con
// un desglose opcional en series. La usan la gráfica y la tabla agrupada, así
// que las dos muestran siempre los mismos números.

export const MEDIDAS = [
  { value: "facturado", label: "Facturado", formato: "monto" },
  { value: "cobrado", label: "Cobrado", formato: "monto" },
  { value: "pendiente", label: "Pendiente de cobro", formato: "monto" },
  { value: "cantidad", label: "Cantidad de facturas", formato: "cantidad" }
];

export function formatearValor(valor, formato, simbolo) {
  if (valor == null) return "—";
  if (formato === "monto") return `${simbolo} ${formatMonto(Math.round(valor))}`;
  return formatMonto(valor);
}

const TRAMOS = [
  { clave: "0-30", etiqueta: "0 a 30 días", hasta: 30 },
  { clave: "31-60", etiqueta: "31 a 60 días", hasta: 60 },
  { clave: "61-90", etiqueta: "61 a 90 días", hasta: 90 },
  { clave: "90+", etiqueta: "Más de 90 días", hasta: Infinity }
];

export const ORIGENES = {
  actividad: "Actividad",
  refacturacion: "Refacturación",
  extension: "Extensión",
  manual: "Manual"
};

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const hoy = () => fechaLocal(new Date().toISOString());

// Cada dimensión dice cómo se agrupa un ítem: `clave` e `etiqueta` reciben el
// ítem y su fecha de referencia (la de emisión).
export const DIMENSIONES = [
  {
    value: "mes",
    label: "Mes",
    temporal: true,
    clave: (_, fecha) => fecha.slice(0, 7),
    etiqueta: (clave) => {
      const [anio, mes] = clave.split("-");
      return `${MESES[Number(mes) - 1]} ${anio}`;
    }
  },
  {
    value: "trimestre",
    label: "Trimestre",
    temporal: true,
    clave: (_, fecha) =>
      `${fecha.slice(0, 4)}-T${Math.floor((Number(fecha.slice(5, 7)) - 1) / 3) + 1}`,
    etiqueta: (clave) => {
      const [anio, trimestre] = clave.split("-");
      return `${trimestre} ${anio}`;
    }
  },
  {
    value: "cliente",
    label: "Cliente",
    clave: (i) => i.clienteId,
    etiqueta: (_, i) => i.clienteNombre
  },
  {
    value: "certificadoTipo",
    label: "Tipo de certificado",
    clave: (i) => i.certificadoTipoId ?? "sin",
    etiqueta: (_, i) => i.certificadoTipoNombre ?? "Sin tipo de certificado"
  },
  {
    value: "actividadTipo",
    label: "Tipo de actividad",
    clave: (i) => i.actividadTipoId ?? "sin",
    etiqueta: (_, i) => i.actividadTipoNombre ?? "Sin actividad"
  },
  {
    value: "origen",
    label: "Origen",
    clave: (i) => i.origen ?? "manual",
    etiqueta: (clave) => ORIGENES[clave] ?? clave
  },
  {
    // Solo tiene sentido para lo pendiente: cuánto hace que se emitió.
    value: "antiguedad",
    label: "Antigüedad de la deuda",
    soloPara: "pendiente",
    clave: (i) => {
      const dias = diasEntre(fechaLocal(i.fecha), hoy());
      return TRAMOS.find((t) => dias <= t.hasta).clave;
    },
    etiqueta: (clave) => TRAMOS.find((t) => t.clave === clave).etiqueta
  }
];

export const dimensionesPara = (medida) =>
  DIMENSIONES.filter((d) => !d.soloPara || d.soloPara === medida);

// El desglose en series va por dimensiones que no son de tiempo.
export const desglosesPara = (medida, dimension) =>
  dimensionesPara(medida).filter(
    (d) => !d.temporal && d.value !== dimension && d.value !== "antiguedad"
  );

const enRango = (fecha, rango) =>
  fecha != null && fecha >= rango.desde && fecha <= rango.hasta;

// Qué ítems entran en cada medida y con qué fecha se ubican en el tiempo.
function itemsDeMedida(medida, facturas, rango) {
  const vigentes = facturas.filter((i) => !i.anulada);
  const conFecha = (items, campo) =>
    items.map((i) => ({ item: i, fecha: fechaLocal(i[campo]) }));

  switch (medida) {
    case "facturado":
    case "cantidad":
      return conFecha(vigentes, "fecha").filter((x) => enRango(x.fecha, rango));
    case "cobrado":
      // Lo pagado de lo emitido en el período, ubicado por fecha de emisión.
      return conFecha(vigentes.filter((i) => i.fechaPago), "fecha").filter((x) =>
        enRango(x.fecha, rango)
      );
    case "pendiente":
      return conFecha(vigentes.filter((i) => !i.fechaPago), "fecha");
    default:
      return [];
  }
}

// Valor de un grupo de ítems.
function valorDe(medida, items, opciones) {
  if (medida === "cantidad") {
    return new Set(items.map((i) => i.facturaId)).size;
  }
  return items.reduce((suma, i) => suma + montoDe(i, opciones), 0);
}

// Todos los meses o trimestres entre dos fechas, para que los períodos sin
// facturación aparezcan en cero en vez de desaparecer del eje.
function clavesTemporales(dimension, desde, hasta) {
  const claves = [];
  const fecha = aFecha(desde);
  fecha.setDate(1);
  const fin = aFecha(hasta);
  while (fecha <= fin) {
    const texto = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-01`;
    const clave = dimension.clave(null, texto);
    if (claves[claves.length - 1] !== clave) claves.push(clave);
    fecha.setMonth(fecha.getMonth() + 1);
  }
  return claves;
}

// Agrupa por la clave de la dimensión. Si hay más de `maximo` grupos se
// quedan los de mayor total y el resto se junta en "Otros".
function agrupar(entradas, dimension, maximo, totalDe) {
  const grupos = new Map();
  for (const x of entradas) {
    const clave = String(dimension.clave(x.item, x.fecha));
    if (!grupos.has(clave)) {
      grupos.set(clave, {
        clave,
        etiqueta: dimension.etiqueta(clave, x.item),
        entradas: []
      });
    }
    grupos.get(clave).entradas.push(x);
  }

  let lista = [...grupos.values()];
  if (dimension.temporal || dimension.value === "antiguedad") return lista;

  lista.sort((a, b) => (totalDe(b) ?? 0) - (totalDe(a) ?? 0));
  if (maximo && lista.length > maximo) {
    const resto = lista.slice(maximo - 1);
    lista = [
      ...lista.slice(0, maximo - 1),
      { clave: "otros", etiqueta: "Otros", entradas: resto.flatMap((g) => g.entradas) }
    ];
  }
  return lista;
}

/**
 * Agrega los ítems de factura para una gráfica o tabla.
 *
 * @returns {{
 *   categorias: Array<{clave, etiqueta}>,
 *   series: Array<{clave, etiqueta, valores: Array<number>}>,
 *   totales: Array<number>,
 *   formato: "monto" | "cantidad"
 * }}
 */
export function agregar(facturas, rango, configuracion, opciones) {
  const {
    medida,
    dimension: dimensionValue,
    desglose: desgloseValue,
    maxCategorias,
    maxSeries = 8
  } = configuracion;
  const medidaDef = MEDIDAS.find((m) => m.value === medida);
  const dimension = DIMENSIONES.find((d) => d.value === dimensionValue);
  const desglose = DIMENSIONES.find((d) => d.value === desgloseValue) || null;

  const entradas = itemsDeMedida(medida, facturas, rango);
  const valor = (lista) => valorDe(medida, lista.map((x) => x.item), opciones);
  const totalDe = (grupo) => valor(grupo.entradas);

  let categorias = agrupar(entradas, dimension, maxCategorias, totalDe);

  if (dimension.temporal) {
    const porClave = new Map(categorias.map((c) => [c.clave, c]));
    // Lo pendiente no tiene período: el eje va de la deuda más vieja a hoy.
    const fechas = entradas.map((x) => x.fecha).sort();
    const desde = medida === "pendiente" ? fechas[0] : rango.desde;
    const hasta = medida === "pendiente" ? hoy() : rango.hasta;
    categorias = desde
      ? clavesTemporales(dimension, desde, hasta).map(
          (clave) =>
            porClave.get(clave) || {
              clave,
              etiqueta: dimension.etiqueta(clave),
              entradas: []
            }
        )
      : [];
  } else if (dimension.value === "antiguedad") {
    const porClave = new Map(categorias.map((c) => [c.clave, c]));
    categorias = TRAMOS.map(
      (t) => porClave.get(t.clave) || { clave: t.clave, etiqueta: t.etiqueta, entradas: [] }
    );
  }

  const series = desglose
    ? agrupar(entradas, desglose, maxSeries, totalDe).map((serie) => {
        const claves = new Set(serie.entradas);
        return {
          clave: serie.clave,
          etiqueta: serie.etiqueta,
          valores: categorias.map((c) =>
            valor(c.entradas.filter((x) => claves.has(x)))
          )
        };
      })
    : [
        {
          clave: medida,
          etiqueta: medidaDef.label,
          valores: categorias.map((c) => valor(c.entradas))
        }
      ];

  return {
    categorias: categorias.map(({ clave, etiqueta }) => ({ clave, etiqueta })),
    series,
    totales: categorias.map((c) => valor(c.entradas)),
    formato: medidaDef.formato
  };
}
